import { useState, useEffect, useRef, type ReactNode } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faDownload,
  faArrowUpRightFromSquare,
} from '@fortawesome/pro-regular-svg-icons';
import {
  Loader,
  SlideOutPanelShell,
  Stack,
  Text,
  slideOutPanelShellStyles as shellStyles,
  useExitRetained,
  useSlideOutPanelChrome,
} from '@cowbell/ui';
import { Button } from '@cowbell/ui/core';
import { useNotifications } from '../../../../_lib/notifications/useNotifications';
import { getKBDocumentBlob } from '../../api/kb.api';
import { downloadArtifact } from '../../v2/artifact-download';
import { artifactPresignUrl } from '../../v2/artifact-preview-url';
import { triggerBrowserDownload } from '../../utils/download-delivery-result';
import type { Source, PdfMetadata } from '../../types';
import styles from './PDFViewerPanel.module.css';

/** `%PDF-`, the leading bytes of every PDF file. */
const PDF_MAGIC_BYTES = [0x25, 0x50, 0x44, 0x46, 0x2d];

function isInternalApiUrl(url: string): boolean {
  return url.startsWith('/api/');
}

/**
 * Re-type a fetched document blob as a PDF for display, but only when the
 * bytes really are a PDF.
 *
 * An iframe pointed at a `blob:` URL renders nothing at all when the blob's
 * type is `application/octet-stream` — no viewer, no error, just an empty
 * frame. The presign path is unaffected because S3 returns `application/pdf`,
 * but `/api/docs/v1/rag/get_content` serves submission attachments as
 * `octet-stream`, which would leave this panel blank (OMNI-742, verified
 * in-browser).
 *
 * The magic-number check is not paranoia: `get_documents` labels every account
 * document `source_type: "pdf"` regardless of its real fileType, so images
 * reach this panel too. An iframe displays those correctly on its own, and
 * asserting `application/pdf` over them would break a preview that works.
 * Sniffing keeps the override to the case it was written for.
 */
async function asDisplayablePdf(blob: Blob): Promise<Blob> {
  if (blob.type === 'application/pdf') return blob;
  const header = new Uint8Array(
    await blob.slice(0, PDF_MAGIC_BYTES.length).arrayBuffer()
  );
  const isPdf = PDF_MAGIC_BYTES.every((byte, index) => header[index] === byte);
  return isPdf ? new Blob([blob], { type: 'application/pdf' }) : blob;
}

function useAuthenticatedBlobUrl(rawUrl: string, isActive: boolean) {
  const [blob, setBlob] = useState<Blob | null>(null);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const previousUrlRef = useRef<string>('');

  useEffect(() => {
    if (!isActive || !rawUrl) {
      setBlob(null);
      setBlobUrl(null);
      setIsLoading(false);
      setError(null);
      previousUrlRef.current = '';
      return;
    }

    if (!isInternalApiUrl(rawUrl)) {
      setBlob(null);
      setBlobUrl(rawUrl);
      setIsLoading(false);
      setError(null);
      previousUrlRef.current = '';
      return;
    }

    if (rawUrl === previousUrlRef.current) return;
    previousUrlRef.current = rawUrl;

    let revoked = false;
    setIsLoading(true);
    setError(null);
    setBlob(null);
    setBlobUrl((prev) => {
      if (prev && prev.startsWith('blob:')) URL.revokeObjectURL(prev);
      return null;
    });

    getKBDocumentBlob(rawUrl)
      .then(async (response) => {
        if (revoked) return;
        const displayable = await asDisplayablePdf(response.data);
        // Re-check: the sniff above awaits, so the panel may have closed or
        // switched documents while it was in flight.
        if (revoked) return;
        setBlob(displayable);
        setBlobUrl(URL.createObjectURL(displayable));
      })
      .catch((err) => {
        if (revoked) return;
        console.error('KB document fetch failed', err);
        setError('Failed to load document.');
      })
      .finally(() => {
        if (!revoked) setIsLoading(false);
      });

    return () => {
      revoked = true;
      setBlob(null);
      setBlobUrl((prev) => {
        if (prev && prev.startsWith('blob:')) URL.revokeObjectURL(prev);
        return null;
      });
    };
  }, [rawUrl, isActive]);

  return { blob, blobUrl, isLoading, error };
}

interface PDFViewerPanelProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly source: Source | null;
  /** Width of the Ask Moo chat container (px) */
  readonly chatWidth: number;
  /** Right offset of the chat container (px) — needed for floating mode */
  readonly chatRight?: number;
  /** Width (px) the panel opens at until the user drags it. Omitted, the shell's default: two thirds of the main area. */
  readonly defaultWidth?: number;
}

function CenteredMessage({
  children,
  color,
}: Readonly<{ children: ReactNode; color?: string }>) {
  return (
    <Stack align="center" justify="center" h="100%">
      <Text size="sm" c={color}>
        {children}
      </Text>
    </Stack>
  );
}

/** Shared by the "fetch in flight" and "url present but the fetch effect
 *  hasn't run yet" frames — same markup, two trigger conditions. */
/** The header's page count, and the file size beside it when known; nothing before the page count is. */
export function pdfHeaderMeta(meta: PdfMetadata | undefined): string | null {
  if (!meta?.pageCount) {
    return null;
  }
  if (!meta.fileSize) {
    return `${meta.pageCount} pages`;
  }
  return `${meta.pageCount} pages · ${meta.fileSize}`;
}

function LoadingState() {
  return (
    <Stack align="center" justify="center" h="100%">
      <Loader size="md" />
      <Text size="sm" c="dimmed">
        Loading document…
      </Text>
    </Stack>
  );
}

/**
 * PDF preview panel that docks to the LEFT of the Ask Moo chat sidebar.
 * Chrome (backdrop, drag-resize, expand/collapse, focus trap, dialog
 * semantics, Esc) comes from `useSlideOutPanelChrome` + `SlideOutPanelShell`,
 * the pair `FilePreviewPanel`, `CvePanel` and `ThreatActorPanel` use; this
 * file keeps what is particular to a PDF: the authenticated blob, the viewer
 * iframe, and the named Download.
 */
export default function PDFViewerPanel({
  isOpen,
  onClose,
  source,
  chatWidth,
  chatRight = 0,
  defaultWidth,
}: PDFViewerPanelProps) {
  const notifications = useNotifications();

  // On close the caller clears `source` in the same turn; the panel keeps what it showed while it slides away.
  const { value: shown, exiting } = useExitRetained(source, isOpen && !!source);

  const rawFileUrl = shown?.url ?? '';
  const fileName = shown?.title ?? 'Document';
  const pdfMeta = shown?.metadata as PdfMetadata | undefined;

  const chrome = useSlideOutPanelChrome({
    isOpen,
    hasSource: !!source,
    onClose,
    chatWidth,
    chatRight,
    defaultWidth,
  });
  const { visible } = chrome;

  const {
    blob,
    blobUrl,
    isLoading: isBlobLoading,
    error: blobError,
  } = useAuthenticatedBlobUrl(rawFileUrl, visible || exiting);

  // The viewer's own save button names the file after the blob URL, a UUID.
  // This Download names it properly: a generated artifact through its presign
  // route, whose S3 link carries Content-Disposition (the card's own download
  // path); anything else from the fetched blob, with `download` set to the
  // title. External urls have no blob and keep "Open in new tab" only.
  const presignUrl =
    shown?.type === 'artifact' ? artifactPresignUrl(rawFileUrl) : null;
  const canDownload = !!presignUrl || !!blob;

  const handleDownload = async () => {
    if (presignUrl) {
      try {
        await downloadArtifact(presignUrl, fileName);
        return;
      } catch (err) {
        // The blob, when loaded, holds the same bytes and still saves under
        // the right name, so fall through to it before reporting a failure.
        console.error('Artifact download failed', err);
      }
    }
    if (blob) {
      triggerBrowserDownload(blob, fileName);
      return;
    }
    notifications.error("Couldn't download file");
  };

  // Early-return dispatch rather than a chained ternary (repo code style, and
  // the pattern FilePreviewPanel already uses).
  function renderContent(): ReactNode {
    if (blobUrl) {
      // The browser toolbar stays for its zoom, page navigation and search,
      // which the panel cannot drive inside the viewer (zoom is only settable
      // through the URL fragment, at load). Its save button names the file
      // after the blob URL, a UUID, so the header carries a labelled Download
      // that names it correctly.
      return (
        <iframe
          src={`${blobUrl}#toolbar=1&navpanes=0&view=FitH`}
          title={fileName}
          className={styles.iframe}
        />
      );
    }

    if (isBlobLoading) return <LoadingState />;

    if (blobError)
      return <CenteredMessage color="red">{blobError}</CenteredMessage>;

    // The source carried no usable url, so no request was ever made and a
    // spinner here could never resolve (OMNI-742). Keyed on the url itself
    // rather than on the absence of a blob: `!blobUrl` is also true for the
    // frame between mount and the fetch effect running, which would flash
    // this message at a document that goes on to load fine.
    if (!rawFileUrl) {
      return (
        <CenteredMessage color="dimmed">
          Preview isn’t available for this document.
        </CenteredMessage>
      );
    }

    return <LoadingState />;
  }

  return (
    <SlideOutPanelShell
      chrome={chrome}
      onClose={onClose}
      styles={shellStyles}
      headerTitle={fileName}
      headerMeta={pdfHeaderMeta(pdfMeta)}
      headerActionsStart={
        canDownload && (
          <Button
            size="xs"
            variant="light"
            leftSection={<FontAwesomeIcon icon={faDownload} />}
            onClick={handleDownload}
          >
            Download
          </Button>
        )
      }
      headerLink={
        blobUrl && (
          <a
            className={shellStyles.headerBtn}
            href={blobUrl}
            target="_blank"
            rel="noopener noreferrer"
            title="Open in new tab"
            aria-label="Open in new tab"
          >
            <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
          </a>
        )
      }
    >
      {renderContent()}
    </SlideOutPanelShell>
  );
}
